import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { jetAPI, bookingAPI, paymentAPI, messageAPI } from "@/lib/api";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  JetHeader,
  JetDetailsGrid,
  JetAmenities,
  JetDescription,
  JetBookingSidebar,
  BookingFormStep,
  PaymentStep,
  SuccessStep,
  ChatStep
} from "@/components/jet";
import { ListingRouteMap } from "@/components/ListingRouteMap";

// Google OAuth URL
const getGoogleAuthUrl = () => {
  const redirectUrl = window.location.origin + '/';
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// Load Razorpay script
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function JetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [paymentStep, setPaymentStep] = useState("form");
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [bookingForm, setBookingForm] = useState({
    quantity: "1",
    shipper_name: user?.name || "",
  });

  const fetchListing = useCallback(async () => {
    try {
      const response = await jetAPI.getById(id);
      setListing(response.data);
    } catch (error) {
      toast.error("Flight not found");
      navigate("/private-jets");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchMessages = useCallback(async () => {
    if (!currentBooking) return;
    try {
      const response = await messageAPI.getMessages(currentBooking.id);
      setMessages(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch messages:', error.message);
      }
    }
  }, [currentBooking]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  useEffect(() => {
    if (currentBooking?.payment_status === "paid") {
      fetchMessages();
    }
  }, [currentBooking, fetchMessages]);

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please sign in to book");
      window.location.href = getGoogleAuthUrl();
      return;
    }
    
    if (parseInt(bookingForm.quantity) > listing.available_seats) {
      toast.error(`Maximum seats available: ${listing.available_seats}`);
      return;
    }
    
    setBookingLoading(true);
    
    try {
      const response = await bookingAPI.create({
        listing_id: id,
        listing_type: "jet",
        quantity: parseInt(bookingForm.quantity),
        shipper_name: bookingForm.shipper_name,
      });
      
      setCurrentBooking(response.data);
      setPaymentStep("payment");
      toast.success("Booking created! Please complete payment.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking failed");
    } finally {
      setBookingLoading(false);
    }
  };

  const handlePayment = async () => {
    const loaded = await loadRazorpay();
    
    if (!loaded) {
      handleMockPayment();
      return;
    }

    try {
      const orderResponse = await paymentAPI.createOrder(currentBooking.id);
      const orderData = orderResponse.data;

      if (orderData.mock) {
        handleMockPayment();
        return;
      }

      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Setuxpress",
        description: `Private Jet: ${listing.origin_city} to ${listing.destination_city}`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await paymentAPI.verifyPayment({
              booking_id: currentBooking.id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            
            setCurrentBooking(verifyResponse.data.booking);
            setPaymentStep("success");
            toast.success("Payment successful!");
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: user?.name,
          email: user?.email,
        },
        theme: {
          color: "#D4AF37",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error("Failed to initiate payment");
    }
  };

  const handleMockPayment = async () => {
    setBookingLoading(true);
    try {
      const verifyResponse = await paymentAPI.verifyPayment({
        booking_id: currentBooking.id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
        razorpay_order_id: `order_mock_${Date.now()}`,
        razorpay_signature: "mock_signature",
      });
      
      setCurrentBooking(verifyResponse.data.booking);
      setPaymentStep("success");
      toast.success("Payment successful! (Test Mode)");
    } catch (error) {
      toast.error("Payment verification failed");
    } finally {
      setBookingLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await messageAPI.send(currentBooking.id, newMessage);
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleGoToDashboard = () => {
    setBookingOpen(false);
    navigate(user?.user_type === "partner" ? "/partner" : "/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!listing) return null;

  const totalAmount = parseInt(bookingForm.quantity) * listing.price_per_seat;
  const platformFee = totalAmount * 0.02;

  return (
    <div className="min-h-screen bg-[#050505] text-white noise-texture">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
              
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Image */}
      <div className="relative h-[40vh]">
        <img 
          src="https://images.pexels.com/photos/5778221/pexels-photo-5778221.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Private jet"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10 pb-16">
        <Link 
          to="/private-jets" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Listings</span>
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <JetHeader listing={listing} />
            <JetDetailsGrid listing={listing} />
            
            {/* Route Map */}
            <div className="glass overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-bold">Flight Route</h3>
              </div>
              <ListingRouteMap
                originCity={listing.origin_city}
                destinationCity={listing.destination_city}
                originCoords={listing.origin_lat && listing.origin_lng ? { lat: listing.origin_lat, lng: listing.origin_lng } : null}
                destinationCoords={listing.destination_lat && listing.destination_lng ? { lat: listing.destination_lat, lng: listing.destination_lng } : null}
                transportMode="air"
                height="280px"
              />
            </div>
            
            <JetAmenities amenities={listing.amenities} />
            <JetDescription description={listing.description} />
          </div>

          {/* Booking Sidebar */}
          <JetBookingSidebar
            listing={listing}
            bookingOpen={bookingOpen}
            setBookingOpen={setBookingOpen}
            user={user}
          >
            {paymentStep === "form" && (
              <BookingFormStep
                listing={listing}
                bookingForm={bookingForm}
                setBookingForm={setBookingForm}
                totalAmount={totalAmount}
                platformFee={platformFee}
                bookingLoading={bookingLoading}
                onSubmit={handleBooking}
              />
            )}

            {paymentStep === "payment" && currentBooking && (
              <PaymentStep
                currentBooking={currentBooking}
                bookingLoading={bookingLoading}
                onPayment={handlePayment}
              />
            )}

            {paymentStep === "success" && currentBooking && (
              <SuccessStep
                currentBooking={currentBooking}
                user={user}
                onChat={() => setPaymentStep("chat")}
                onDashboard={handleGoToDashboard}
              />
            )}

            {paymentStep === "chat" && currentBooking && (
              <ChatStep
                messages={messages}
                user={user}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                sendingMessage={sendingMessage}
                onSend={handleSendMessage}
                onBack={() => setPaymentStep("success")}
                messagesEndRef={messagesEndRef}
              />
            )}
          </JetBookingSidebar>
        </div>
      </div>
    </div>
  );
}
